import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Calculator } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/StateBlocks";
import { crmService, emitCrmChanged } from "@/services/crmService";
import { RelatedRunSection } from "@/components/run/RelatedRunSection";
import { txTypeLabel, sideLabel, txStatusLabel } from "@/config/crmOptions";
import { fmtCents, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const Detail = ({ label, value }) => (
  <div><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-0.5 text-sm text-foreground">{value ?? "—"}</p></div>
);
const dollars = (c) => (c == null ? "" : c / 100);

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'close' | 'cancel' | 'paid'
  const [comm, setComm] = useState({ calculation_type: "percentage", price: "", rate: "", split_type: "none", split_rate: "", split_amount: "", referral: "", txfee: "", other: "" });

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const t = await crmService.getTransaction(id);
      setTx(t);
      if (t.contact?.id) crmService.contactActivity(t.contact.id).then(setActivity).catch(() => {});
      const c = t.commission;
      setComm({
        calculation_type: c?.calculation_type || "percentage",
        price: c ? dollars(c.transaction_price) : dollars(t.sale_price),
        rate: c?.gross_commission_rate ?? "", split_type: c?.brokerage_split_type || "none",
        split_rate: c?.brokerage_split_rate ?? "", split_amount: c ? dollars(c.brokerage_split_amount) : "",
        referral: c ? dollars(c.referral_fee) : "", txfee: c ? dollars(c.transaction_fee) : "", other: c ? dollars(c.other_adjustments) : "",
      });
    } catch { setError(true); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); emitCrmChanged(); setConfirm(null); await load(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const saveCommission = async () => {
    const toCents = (v) => (v === "" || v == null ? 0 : Math.round(Number(v) * 100));
    await act(() => crmService.saveCommission(id, {
      calculation_type: comm.calculation_type,
      transaction_price: toCents(comm.price),
      gross_commission_rate: comm.calculation_type === "percentage" ? Number(comm.rate || 0) : null,
      gross_commission_amount: comm.calculation_type === "fixed" ? toCents(comm.rate) : null,
      brokerage_split_type: comm.split_type,
      brokerage_split_rate: comm.split_type === "percentage" ? Number(comm.split_rate || 0) : null,
      brokerage_split_amount: comm.split_type === "fixed" ? toCents(comm.split_amount) : null,
      referral_fee: toCents(comm.referral), transaction_fee: toCents(comm.txfee), other_adjustments: toCents(comm.other),
    }), "Commission saved");
  };

  if (loading) return <AppShell><div className="py-10"><LoadingState label="Loading transaction" /></div></AppShell>;
  if (error || !tx) return <AppShell><ErrorState title="Transaction not found" description="It may have been removed or is outside your workspace." onRetry={load} /></AppShell>;

  const c = tx.commission;
  const isClosed = tx.status === "closed";
  const isCancelled = tx.status === "cancelled";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="transaction-detail-page">
        <button onClick={() => navigate("/transactions")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="tx-back"><ArrowLeft className="h-4 w-4" /> Back to Transactions</button>

        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-card sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-foreground" data-testid="tx-title">{tx.property_address_line1}</h1>
              <Badge className="bg-brand-navy text-white hover:bg-brand-navy" data-testid="tx-status-badge">{txStatusLabel(tx.status)}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {tx.contact?.first_name} {tx.contact?.last_name} · {txTypeLabel(tx.transaction_type)} · {sideLabel(tx.representation_side)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isClosed && !isCancelled && <Button onClick={() => setConfirm("close")} data-testid="tx-close-btn"><CheckCircle2 className="h-4 w-4" /> Close</Button>}
            {!isClosed && !isCancelled && <Button variant="ghost" className="text-destructive" onClick={() => setConfirm("cancel")} data-testid="tx-cancel-btn"><XCircle className="h-4 w-4" /> Cancel</Button>}
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList data-testid="tx-tabs-detail">
            <TabsTrigger value="overview" data-testid="tx-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="commission" data-testid="tx-tab-commission">Commission</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tx-tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-4">
            <Card><CardContent className="grid gap-5 p-6 sm:grid-cols-2 lg:grid-cols-3">
              <Detail label="Client" value={`${tx.contact?.first_name || ""} ${tx.contact?.last_name || ""}`} />
              <Detail label="Property" value={[tx.property_address_line1, tx.city, tx.state, tx.postal_code].filter(Boolean).join(", ")} />
              <Detail label="Type" value={txTypeLabel(tx.transaction_type)} />
              <Detail label="Representation" value={sideLabel(tx.representation_side)} />
              <Detail label="Status" value={txStatusLabel(tx.status)} />
              <Detail label="Price" value={fmtCents(tx.sale_price)} />
              <Detail label="Contract date" value={fmtDate(tx.contract_date)} />
              <Detail label="Expected closing" value={fmtDate(tx.expected_closing_date)} />
              <Detail label="Actual closing" value={fmtDate(tx.actual_closing_date)} />
            </CardContent></Card>
            <div className="mt-6">
              <RelatedRunSection entityType="transaction" entityId={tx.id}
                related={{ type: "transaction", id: tx.id, contact_id: tx.contact?.id, label: tx.property_address_line1 }} />
            </div>
          </TabsContent>

          <TabsContent value="commission" className="space-y-4 pt-4">
            <Card><CardContent className="grid gap-5 p-6 sm:grid-cols-3">
              <Detail label="Gross commission" value={c ? fmtCents(c.gross_commission_amount) : "—"} />
              <Detail label="Brokerage split" value={c ? fmtCents(c.brokerage_split_amount) : "—"} />
              <Detail label={c?.status === "paid" ? "Paid agent net" : "Estimated agent net"} value={c ? fmtCents(c.status === "paid" ? c.final_agent_net : c.estimated_agent_net) : "—"} />
              <Detail label="Status" value={c ? c.status : "No commission yet"} />
              <Detail label="Referral / fees" value={c ? fmtCents((c.referral_fee || 0) + (c.transaction_fee || 0) + (c.other_adjustments || 0)) : "—"} />
              <Detail label="Paid date" value={fmtDate(c?.paid_at)} />
            </CardContent></Card>

            {c?.status !== "paid" && c?.status !== "cancelled" && (
              <Card><CardContent className="space-y-4 p-6">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground"><Calculator className="h-4 w-4" /> {c ? "Update" : "Add"} commission estimate</div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Select value={comm.calculation_type} onValueChange={(v) => setComm((s) => ({ ...s, calculation_type: v }))}>
                    <SelectTrigger data-testid="comm-calc-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed amount</SelectItem></SelectContent>
                  </Select>
                  <Input placeholder="Transaction price ($)" type="number" value={comm.price} onChange={(e) => setComm((s) => ({ ...s, price: e.target.value }))} data-testid="comm-price" />
                  <Input placeholder={comm.calculation_type === "percentage" ? "Commission %" : "Commission amount ($)"} type="number" value={comm.rate} onChange={(e) => setComm((s) => ({ ...s, rate: e.target.value }))} data-testid="comm-rate" />
                  <Select value={comm.split_type} onValueChange={(v) => setComm((s) => ({ ...s, split_type: v }))}>
                    <SelectTrigger data-testid="comm-split-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No brokerage split</SelectItem><SelectItem value="percentage">Split %</SelectItem><SelectItem value="fixed">Split $</SelectItem></SelectContent>
                  </Select>
                  {comm.split_type === "percentage" && <Input placeholder="Split %" type="number" value={comm.split_rate} onChange={(e) => setComm((s) => ({ ...s, split_rate: e.target.value }))} data-testid="comm-split-rate" />}
                  {comm.split_type === "fixed" && <Input placeholder="Split $" type="number" value={comm.split_amount} onChange={(e) => setComm((s) => ({ ...s, split_amount: e.target.value }))} data-testid="comm-split-amount" />}
                  <Input placeholder="Referral fee ($)" type="number" value={comm.referral} onChange={(e) => setComm((s) => ({ ...s, referral: e.target.value }))} data-testid="comm-referral" />
                  <Input placeholder="Transaction fee ($)" type="number" value={comm.txfee} onChange={(e) => setComm((s) => ({ ...s, txfee: e.target.value }))} data-testid="comm-txfee" />
                  <Input placeholder="Other adjustments ($)" type="number" value={comm.other} onChange={(e) => setComm((s) => ({ ...s, other: e.target.value }))} data-testid="comm-other" />
                </div>
                <Button onClick={saveCommission} disabled={busy} data-testid="comm-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Commission"}</Button>
              </CardContent></Card>
            )}

            {c && (c.status === "pending" || c.status === "estimated") && isClosed && (
              <Button onClick={() => setConfirm("paid")} data-testid="comm-mark-paid"><CheckCircle2 className="h-4 w-4" /> Mark Commission Paid</Button>
            )}
          </TabsContent>

          <TabsContent value="activity" className="pt-4">
            {activity.length === 0 ? <EmptyState title="No activity yet" description="Transaction and lifecycle events will appear here." /> : (
              <div className="space-y-3" data-testid="tx-activity">
                {activity.map((a) => (
                  <div key={a.id} className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3">
                    <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-gold" />
                    <div><p className="text-sm font-medium text-foreground">{a.type.replace(/_/g, " ")}</p><p className="text-xs text-muted-foreground">{a.actor_name} · {fmtDateTime(a.created_at)}</p></div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="tx-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm === "close" && "Close this transaction?"}
              {confirm === "cancel" && "Cancel this transaction?"}
              {confirm === "paid" && "Mark commission paid?"}
            </DialogTitle>
            <DialogDescription>
              {confirm === "close" && "This updates the transaction to closed, marks the client a past client, moves the linked lead to Closed, and moves commission to pending."}
              {confirm === "cancel" && "This cancels the transaction and any estimated/pending commission. The client and lead stay usable."}
              {confirm === "paid" && "This records the commission as paid. Do this only after you've actually received funds."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            {confirm === "close" && <Button onClick={() => act(() => crmService.closeTransaction(id), "Transaction closed")} disabled={busy} data-testid="tx-confirm-close">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Close Transaction"}</Button>}
            {confirm === "cancel" && <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => act(() => crmService.cancelTransaction(id), "Transaction cancelled")} disabled={busy} data-testid="tx-confirm-cancel">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel Transaction"}</Button>}
            {confirm === "paid" && <Button onClick={() => act(() => crmService.markCommissionPaid(id), "Commission marked paid")} disabled={busy} data-testid="tx-confirm-paid">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark Paid"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
